import React, { useEffect, useRef, useState } from 'react';
import Phone from '../components/Phone';
import WaitlistService from '../services/WaitlistService';
import allergenWebp from '../assets/screens/allergen.webp';
import allergenPng from '../assets/screens/allergen.png';

/**
 * A demand test, not a product page (docs/plans/PIVOT_RESEARCH.md): does
 * "cooking with several intolerances at once" find people who want it, and
 * would they pay? German only, reachable by URL only - it is not in the
 * navigation. Three numbers decide it: views, price clicks, signups.
 *
 * The price button is a fake door, and it says so the moment it is pressed:
 * nothing is sold, nothing is promised that does not exist.
 */
const TEST = 'vertraeglich';

/** `utm_source` (or `src`) from the address, as a short code, or null. */
const trafficSource = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get('utm_source') || params.get('src') || '').trim().toLowerCase();
    return /^[a-z0-9][a-z0-9_-]{0,23}$/.test(raw) ? raw : null;
  } catch {
    return null;
  }
};

const INTOLERANCES = [
  ['histamin', 'Histamin'],
  ['fruktose', 'Fruktose'],
  ['laktose', 'Laktose'],
  ['gluten', 'Gluten / Zöliakie'],
  ['sorbit', 'Sorbit'],
  ['fodmap', 'Reizdarm / FODMAP'],
  ['andere', 'Etwas anderes'],
];

const ERRORS = {
  invalid_email: 'Das sieht nicht nach einer E-Mail-Adresse aus.',
  network: 'Keine Verbindung. Bitte versuch es gleich nochmal.',
  rate_limited: 'Zu viele Versuche. Bitte warte eine Minute.',
  server: 'Das hat nicht geklappt. Bitte versuch es später nochmal.',
};

const VertraeglichPage = () => {
  const [email, setEmail] = useState('');
  const [picked, setPicked] = useState([]);
  const [priceClicked, setPriceClicked] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [error, setError] = useState('');
  const formRef = useRef(null);

  useEffect(() => {
    const previousTitle = document.title;
    const previousLang = document.documentElement.lang;
    document.title = 'Kochen mit Histamin-, Fruktose- und Laktoseintoleranz – Warteliste';
    document.documentElement.lang = 'de';
    WaitlistService.track(TEST, 'view');
    return () => {
      document.title = previousTitle;
      document.documentElement.lang = previousLang;
    };
  }, []);

  const toggle = (key) =>
    setPicked((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );

  const pressPrice = () => {
    if (!priceClicked) WaitlistService.track(TEST, 'price_click');
    setPriceClicked(true);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const submit = async (event) => {
    event.preventDefault();
    setStatus('loading');
    setError('');
    try {
      await WaitlistService.join({
        test: TEST,
        email,
        // Which channel brought them: `?utm_source=` on the link we posted.
        // A short word or nothing - never the whole query string.
        answers: { intolerances: picked, source: trafficSource() },
        priceClick: priceClicked,
        locale: navigator.language,
      });
      setStatus('done');
    } catch (e) {
      setStatus('error');
      setError(ERRORS[e.message] || ERRORS.server);
    }
  };

  return (
    <div className="vt">
      <section className="hero vt-hero">
        <div className="hero-copy">
          <div className="chip">
            <span className="chip-dot" />
            In Arbeit · für iPhone · auf Deutsch
          </div>
          <h1 className="h1">
            Was koche ich heute –
            <br />
            mit meinen Unverträglich&shy;keiten?
          </h1>
          <p className="lead">
            Histamin, Fruktose und Laktose gleichzeitig: Lebensmittellisten sagen dir, was nicht
            geht. Wir bauen die App, die dir sagt, was <em>geht</em> – Rezepte, Wochenplan und
            Einkaufsliste, die alle deine Unverträglichkeiten auf einmal beachten.
          </p>
          <div className="hero-ctas">
            <a href="#warteliste" className="cta">
              Auf die Warteliste
            </a>
            <a href="#preis" className="cta-secondary">
              Was kostet das?
            </a>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-glow" />
          <Phone
            webp={allergenWebp}
            png={allergenPng}
            alt="Ein Rezept mit markierten Unverträglichkeiten"
          />
        </div>
      </section>

      <section className="section">
        <div className="section-inner">
          <div className="eyebrow">Was sie kann</div>
          <h2 className="h2 section-headline">Kochen statt nachschlagen.</h2>
          <div className="vt-grid">
            <div className="card">
              <h3 className="h3">Mehrere Unverträglichkeiten auf einmal</h3>
              <p className="body-copy">
                Du stellst einmal ein, was du nicht verträgst. Jedes Rezept, jeder Vorschlag und
                jede Einkaufsliste hält sich an die Kombination – nicht nur an eine Liste.
              </p>
            </div>
            <div className="card">
              <h3 className="h3">Jedes Rezept wird verträglich</h3>
              <p className="body-copy">
                Ein Rezept von einem Blog, aus Instagram oder aus Omas Kochbuch fotografiert: Die
                App schreibt es um, nennt jeden Tausch und sagt ehrlich, wenn ein Gericht nicht zu
                retten ist.
              </p>
            </div>
            <div className="card">
              <h3 className="h3">Aus dem, was da ist</h3>
              <p className="body-copy">
                Drei Ideen aus deinem Vorrat, passend zu deinen Unverträglichkeiten. Ein Wochenplan,
                der nicht jeden Tag dasselbe vorschlägt.
              </p>
            </div>
            <div className="card">
              <h3 className="h3">Tagebuch, das Muster findet</h3>
              <p className="body-copy">
                Mahlzeiten und Beschwerden in zwei Tipps festhalten, auch Selbstgekochtes. Nach ein
                paar Wochen siehst du, was dir wirklich nicht bekommt.
              </p>
            </div>
            <div className="card">
              <h3 className="h3">Deine Gesundheitsdaten bleiben bei dir</h3>
              <p className="body-copy">
                Tagebuch, Vorrat und Einstellungen liegen auf deinem iPhone, nicht auf unseren
                Servern. Kein Konto nötig.
              </p>
            </div>
            <div className="card">
              <h3 className="h3">Kein Ersatz für die Ernährungsberatung</h3>
              <p className="body-copy">
                Die App kocht mit dir, sie stellt keine Diagnosen. Die Einstufungen stammen aus
                benannten Quellen und sind vorsichtig gewählt.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="preis">
        <div className="section-inner vt-narrow">
          <div className="eyebrow">Preis</div>
          <h2 className="h2 section-headline">
            Ein Abo, das weniger kostet als ein verdorbenes Abendessen.
          </h2>
          <div className="card vt-price">
            <div className="price-line">
              <span className="price-num mono">4,99 €</span>
              <span className="price-period">pro Monat für Gründungsmitglieder</span>
            </div>
            <p className="body-copy">
              Später 9,99 € im Monat. Wer auf der Warteliste steht, behält den Gründerpreis, solange
              das Abo läuft.
            </p>
            <button type="button" className="cta vt-price-cta" onClick={pressPrice}>
              Gründerpreis sichern
            </button>
            {priceClicked && (
              <p className="body-copy vt-note" role="status">
                Noch kann man nichts kaufen – die App ist in Arbeit. Trag dich unten ein, dann ist
                dir der Gründerpreis sicher und du erfährst als Erste:r, wann es losgeht.
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="section" id="warteliste" ref={formRef}>
        <div className="section-inner vt-narrow">
          <div className="eyebrow">Warteliste</div>
          <h2 className="h2 section-headline">Sag uns, was du nicht verträgst.</h2>
          {status === 'done' ? (
            <div className="card" role="status">
              <h3 className="h3">Danke – du stehst drauf.</h3>
              <p className="body-copy">
                Wir schreiben dir genau einmal, wenn es etwas zu testen gibt. Bis dahin: keine
                Newsletter, keine Weitergabe.
              </p>
            </div>
          ) : (
            <form className="card vt-form" onSubmit={submit} noValidate>
              <fieldset className="vt-fieldset">
                <legend className="body-copy">Womit kochst du? Mehrere möglich.</legend>
                <div className="vt-options">
                  {INTOLERANCES.map(([key, label]) => (
                    <label key={key} className={`vt-option${picked.includes(key) ? ' is-on' : ''}`}>
                      <input
                        type="checkbox"
                        checked={picked.includes(key)}
                        onChange={() => toggle(key)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="body-copy" htmlFor="vt-email">
                Deine E-Mail-Adresse
              </label>
              <input
                id="vt-email"
                className="vt-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="du@beispiel.ch"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="cta" disabled={status === 'loading'}>
                {status === 'loading' ? 'Einen Moment…' : 'Auf die Warteliste'}
              </button>
              {status === 'error' && (
                <p className="body-copy vt-error" role="alert">
                  {error}
                </p>
              )}
              <p className="body-copy vt-small">
                Wir speichern nur deine Adresse und deine Auswahl, auf unserem eigenen Server, und
                löschen sie auf Wunsch: privacy@snapcipe.app.
              </p>
            </form>
          )}
        </div>
      </section>

      <section className="section">
        <div className="section-inner vt-narrow">
          <div className="eyebrow">Ehrlich gefragt</div>
          <h2 className="h2 section-headline">Gibt es die App schon?</h2>
          <p className="body-copy">
            Die Grundlage gibt es: Snapcipe ist im App Store, erkennt Rezepte aus Fotos und Links
            und beachtet heute schon Ernährungsweisen und Allergien. Die Ausrichtung auf mehrere
            Unverträglichkeiten gleichzeitig bauen wir, wenn genug Menschen sie wollen. Deshalb
            diese Seite: Deine Anmeldung entscheidet mit.
          </p>
        </div>
      </section>
    </div>
  );
};

export default VertraeglichPage;
